import { Client } from '@microsoft/microsoft-graph-client';
import { loginRequest, graphConfig, calendarSyncConfig } from './msalConfig';
import { supabase } from '../lib/supabase';
import { Browser } from '@capacitor/browser';
import { isNative, isIOS } from './platformService';
import { storeOAuthState, clearOAuthState } from './storageService';

/**
 * Outlook Calendar Service - Server-Side OAuth Flow
 *
 * This service implements the Confidential Client OAuth flow:
 * 1. Frontend constructs Microsoft OAuth URL and opens popup
 * 2. User authenticates with Microsoft
 * 3. Microsoft redirects to callback page with authorization CODE
 * 4. Callback page sends code to oauth-exchange edge function
 * 5. Server exchanges code for tokens using client_secret
 * 6. Tokens are encrypted and stored - NEVER seen by browser
 *
 * This is more secure than the public client (MSAL) flow because:
 * - Access/refresh tokens never touch the browser
 * - Client secret provides additional security
 * - Follows Microsoft's recommendation for apps with backends
 *
 * Native App Flow (iOS/Android):
 * Uses Capacitor Browser plugin with Universal Links for OAuth callback.
 * The callback URL triggers a deep link back into the app.
 */

// Microsoft OAuth configuration
const MICROSOFT_AUTH_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
const CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID || '';

/**
 * Detect if running in iOS standalone mode (PWA saved to home screen)
 * In standalone mode, window.open() is blocked/restricted
 */
const isIOSStandalone = () => {
  // If running as native app, not a PWA
  if (isNative()) {
    return false;
  }
  // Check for iOS standalone mode (Safari PWA)
  if (window.navigator.standalone === true) {
    return true;
  }
  // Check for display-mode: standalone (works for Android PWA too)
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  return false;
};

/**
 * Check if we should use in-app browser (native) or popup (web)
 */
const shouldUseInAppBrowser = () => {
  return isNative();
};

/**
 * Generate a cryptographically random string
 */
const generateRandomString = (length = 32) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Generate PKCE code verifier (43-128 characters, URL-safe)
 */
const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  // Base64url encode without padding
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

/**
 * Generate PKCE code challenge from verifier (S256 method)
 */
const generateCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  // Base64url encode without padding
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

/**
 * Build the Microsoft OAuth authorization URL with PKCE
 */
const buildAuthUrl = async (redirectUri, state, codeChallenge) => {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    redirect_uri: redirectUri,
    scope: loginRequest.scopes.join(' '),
    response_mode: 'query',
    state: state,
    prompt: 'select_account',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });

  return `${MICROSOFT_AUTH_URL}?${params.toString()}`;
};

/**
 * Authenticate with Microsoft using server-side OAuth flow with PKCE
 *
 * Opens a popup to Microsoft login, then the callback page handles
 * sending the authorization code to the server for token exchange.
 *
 * In iOS standalone mode (PWA), uses redirect flow instead of popup
 * since window.open() is blocked in that environment.
 *
 * In native apps (iOS/Android), uses Capacitor Browser plugin with
 * Universal Links for the OAuth callback.
 *
 * PKCE (Proof Key for Code Exchange) is required by Microsoft for SPA
 * redirect URIs when doing server-side code exchange.
 *
 * @returns {Promise<{calendarEmail: string, connectionId: string}>}
 */
export const authenticateWithMicrosoft = async (existingPopup = null) => {
  // Check flow type BEFORE any async operations (these are synchronous checks)
  const useInAppBrowser = shouldUseInAppBrowser();
  const useRedirectFlow = isIOSStandalone();

  // Use existing popup if provided (from connectCalendar), otherwise open a new one
  // The popup MUST be opened synchronously from click handler to avoid Safari blocker
  let popup = existingPopup;
  if (!useInAppBrowser && !useRedirectFlow && !popup) {
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    // Open popup immediately with loading state (MUST be synchronous)
    popup = window.open(
      'about:blank',
      'outlook-oauth',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      throw new Error('Failed to open popup. Please allow popups for this site.');
    }

    // Show loading state while we prepare OAuth
    popup.document.write(`
      <!DOCTYPE html>
      <html>
        <head><title>Connecting to Microsoft...</title></head>
        <body style="display:flex;justify-content:center;align-items:center;height:100vh;margin:0;font-family:system-ui,-apple-system,sans-serif;background:#f5f5f5;">
          <div style="text-align:center;">
            <div style="width:40px;height:40px;border:3px solid #e0e0e0;border-top-color:#0078d4;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 16px;"></div>
            <p style="color:#666;margin:0;">Connecting to Microsoft...</p>
          </div>
          <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
        </body>
      </html>
    `);
  }

  // Now do async operations (PKCE generation, state storage)
  // Generate PKCE code verifier and challenge
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Generate state for CSRF protection
  const state = generateRandomString();

  // Build redirect URI and auth URL with PKCE
  // For native apps, use the production domain for Universal Links
  const baseUrl = isNative()
    ? 'https://teesync.co.uk'
    : window.location.origin;
  const redirectUri = `${baseUrl}/auth/callback/outlook`;
  const authUrl = await buildAuthUrl(redirectUri, state, codeChallenge);

  // Native app flow - use Capacitor Browser plugin
  if (useInAppBrowser) {
    // Store OAuth state using cross-platform storage service
    await storeOAuthState('outlook', {
      state,
      codeVerifier,
      isNativeMode: true,
    });

    // The promise will be resolved by the App URL listener in App.jsx
    // when the Universal Link callback is received
    return new Promise((resolve, reject) => {
      // Store resolve/reject functions for the App URL listener to call
      window._outlookOAuthResolve = resolve;
      window._outlookOAuthReject = reject;

      // Open in-app browser
      Browser.open({ url: authUrl }).catch((err) => {
        delete window._outlookOAuthResolve;
        delete window._outlookOAuthReject;
        reject(new Error('Failed to open browser: ' + err.message));
      });

      // Timeout after 5 minutes
      setTimeout(() => {
        if (window._outlookOAuthResolve) {
          delete window._outlookOAuthResolve;
          delete window._outlookOAuthReject;
          Browser.close().catch(() => {});
          reject(new Error('Authentication timed out'));
        }
      }, 5 * 60 * 1000);
    });
  }

  // In iOS standalone mode (PWA), use redirect flow instead of popup
  // window.open() is blocked/restricted in standalone mode
  if (useRedirectFlow) {
    // Store OAuth state with redirect mode flag
    await storeOAuthState('outlook', {
      state,
      codeVerifier,
      isRedirectMode: true,
    });
    // Redirect the current page to Microsoft login
    window.location.href = authUrl;
    // Return a promise that never resolves (page is redirecting)
    return new Promise(() => {});
  }

  // Standard popup flow - popup was already opened above
  // Store OAuth state
  await storeOAuthState('outlook', { state, codeVerifier });

  // Check if popup was closed while we were doing async operations
  if (popup.closed) {
    throw new Error('Popup was closed before authentication could complete.');
  }

  // Navigate popup to Microsoft OAuth URL
  popup.location.href = authUrl;

  return new Promise((resolve, reject) => {
    // Track whether authentication has completed
    let isCompleted = false;

    // Set up BroadcastChannel as fallback for when window.opener is null
    // This happens when COOP headers break the opener relationship after cross-origin navigation
    let broadcastChannel = null;
    try {
      broadcastChannel = new BroadcastChannel('outlook-oauth-channel');
      broadcastChannel.onmessage = (event) => {
        if (event.data.type === 'OUTLOOK_OAUTH_SUCCESS') {
          isCompleted = true;
          window.removeEventListener('message', handleMessage);
          clearTimeout(timeoutTimer);
          broadcastChannel.close();
          resolve({
            calendarEmail: event.data.calendarEmail,
            connectionId: event.data.connectionId,
          });
        } else if (event.data.type === 'OUTLOOK_OAUTH_ERROR') {
          isCompleted = true;
          window.removeEventListener('message', handleMessage);
          clearTimeout(timeoutTimer);
          broadcastChannel.close();
          reject(new Error(event.data.error || 'OAuth failed'));
        }
      };
    } catch {
      // BroadcastChannel not supported, will fall back to postMessage only
      console.warn('BroadcastChannel not supported, using postMessage only');
    }

    // Listen for messages from the callback page (primary method)
    const handleMessage = (event) => {
      // Verify origin
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'OUTLOOK_OAUTH_SUCCESS') {
        isCompleted = true;
        window.removeEventListener('message', handleMessage);
        clearTimeout(timeoutTimer);
        if (broadcastChannel) broadcastChannel.close();
        resolve({
          calendarEmail: event.data.calendarEmail,
          connectionId: event.data.connectionId,
        });
      } else if (event.data.type === 'OUTLOOK_OAUTH_ERROR') {
        isCompleted = true;
        window.removeEventListener('message', handleMessage);
        clearTimeout(timeoutTimer);
        if (broadcastChannel) broadcastChannel.close();
        reject(new Error(event.data.error || 'OAuth failed'));
      }
    };

    window.addEventListener('message', handleMessage);

    // Note: We intentionally do NOT poll popup.closed because Cross-Origin-Opener-Policy
    // (COOP) blocks access to popup.closed when the popup navigates to Microsoft's domain,
    // causing console errors. Instead, we rely on:
    // - BroadcastChannel (works even when window.opener is null)
    // - postMessage as fallback
    // - Timeout as final fallback (5 minutes)

    // Timeout after 5 minutes
    const timeoutTimer = setTimeout(() => {
      if (!isCompleted) {
        window.removeEventListener('message', handleMessage);
        if (broadcastChannel) broadcastChannel.close();
        // Try to close popup, but don't check popup.closed to avoid COOP errors
        try {
          popup.close();
        } catch {
          // Ignore - popup may already be closed or COOP may block this
        }
        reject(new Error('Authentication timed out'));
      }
    }, 5 * 60 * 1000);
  });
};

// Get Graph client with authentication
const getAuthenticatedGraphClient = (accessToken) => {
  return Client.init({
    authProvider: (done) => {
      done(null, accessToken);
    }
  });
};

// Fetch user's calendar email
export const fetchCalendarEmail = async (accessToken) => {
  try {
    const client = getAuthenticatedGraphClient(accessToken);
    const user = await client.api('/me').get();
    return user.mail || user.userPrincipalName;
  } catch (error) {
    console.error('Error fetching user email:', error);
    throw error;
  }
};

// Fetch calendar events from Microsoft Graph
export const fetchOutlookCalendarEvents = async (accessToken, startDate, endDate) => {
  try {
    const client = getAuthenticatedGraphClient(accessToken);
    
    // Format dates for Graph API (ISO 8601)
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    
    // Build query parameters
    const queryParams = {
      startDateTime: start.toISOString(),
      endDateTime: end.toISOString(),
      $select: 'id,subject,start,end,isAllDay,showAs,isCancelled,responseStatus',
      $orderby: 'start/dateTime',
      $top: 500 // Maximum events to fetch
    };
    
    // Fetch events
    const events = await client
      .api('/me/calendarview')
      .query(queryParams)
      .get();
    
    return events.value || [];
  } catch (error) {
    console.error('Error fetching calendar events:', error);
    throw error;
  }
};

// Convert Outlook events to TeeSync availability format
export const convertOutlookEventsToAvailability = (events, syncSettings = {}) => {
  const availability = {};
  const settings = { ...calendarSyncConfig, ...syncSettings };
  
  events.forEach(event => {
    // Skip if event doesn't meet criteria
    if (settings.excludeAllDayEvents && event.isAllDay) return;
    if (settings.excludeCancelledEvents && event.isCancelled) return;
    if (settings.onlySyncBusyEvents && !['busy', 'tentative', 'oof'].includes(event.showAs)) return;
    
    // Skip if user declined the event
    if (event.responseStatus?.response === 'declined') return;
    
    const eventStart = new Date(event.start.dateTime);
    const eventEnd = new Date(event.end.dateTime);
    
    // Skip if outside sync hours
    const startHour = eventStart.getHours();
    const endHour = eventEnd.getHours();
    if (startHour < settings.syncStartHour || endHour > settings.syncEndHour) {
      // Adjust times to fit within sync hours
      if (startHour < settings.syncStartHour) {
        eventStart.setHours(settings.syncStartHour, 0, 0, 0);
      }
      if (endHour > settings.syncEndHour) {
        eventEnd.setHours(settings.syncEndHour, 0, 0, 0);
      }
    }
    
    // Get date key (YYYY-MM-DD)
    const dateKey = eventStart.toISOString().split('T')[0];
    
    // Initialize array for this date if not exists
    if (!availability[dateKey]) {
      availability[dateKey] = [];
    }
    
    // Add busy time range
    availability[dateKey].push({
      start: `${String(eventStart.getHours()).padStart(2, '0')}:${String(eventStart.getMinutes()).padStart(2, '0')}`,
      end: `${String(eventEnd.getHours()).padStart(2, '0')}:${String(eventEnd.getMinutes()).padStart(2, '0')}`,
      isCalendarSynced: true,
      calendarEventId: event.id,
      calendarProvider: 'outlook'
    });
  });
  
  // Merge overlapping time ranges for each date
  Object.keys(availability).forEach(date => {
    availability[date] = mergeOverlappingRanges(availability[date]);
  });
  
  return availability;
};

// Merge overlapping time ranges
const mergeOverlappingRanges = (ranges) => {
  if (ranges.length <= 1) return ranges;
  
  // Sort by start time
  const sorted = [...ranges].sort((a, b) => a.start.localeCompare(b.start));
  const merged = [sorted[0]];
  
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];
    
    // Convert times to minutes for comparison
    const [lastEndHour, lastEndMin] = last.end.split(':').map(Number);
    const [currStartHour, currStartMin] = current.start.split(':').map(Number);
    const lastEndMinutes = lastEndHour * 60 + lastEndMin;
    const currStartMinutes = currStartHour * 60 + currStartMin;
    
    // Check if ranges overlap or are adjacent
    if (currStartMinutes <= lastEndMinutes) {
      // Merge the ranges
      const [currEndHour, currEndMin] = current.end.split(':').map(Number);
      const currEndMinutes = currEndHour * 60 + currEndMin;
      
      if (currEndMinutes > lastEndMinutes) {
        last.end = current.end;
      }
      
      // Preserve calendar sync metadata
      if (current.isCalendarSynced) {
        last.isCalendarSynced = true;
        last.calendarEventId = last.calendarEventId || current.calendarEventId;
        last.calendarProvider = 'outlook';
      }
    } else {
      merged.push(current);
    }
  }
  
  return merged;
};

// Get calendar connection from database
export const getCalendarConnection = async (provider = 'outlook') => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');
    
    const { data, error } = await supabase
      .from('calendar_connections')
      .select('*')
      .eq('user_id', user.id)
      .eq('provider', provider)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error getting calendar connection:', error);
    return null;
  }
};

// Disconnect calendar
export const disconnectCalendar = async (provider = 'outlook') => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Delete from database
    const { error } = await supabase
      .from('calendar_connections')
      .delete()
      .eq('user_id', user.id)
      .eq('provider', provider);

    if (error) throw error;

    // Clear OAuth state using cross-platform storage service
    await clearOAuthState('outlook');

    return true;
  } catch (error) {
    console.error('Error disconnecting calendar:', error);
    throw error;
  }
};

// Check if token needs refresh (for reference - actual refresh happens server-side)
export const isTokenExpired = (expiresAt) => {
  const now = new Date();
  const expiry = new Date(expiresAt);
  // Consider expired if less than 5 minutes remaining
  return (expiry - now) < 5 * 60 * 1000;
};

/**
 * NOTE: Token refresh is now handled entirely server-side by the edge functions.
 * The frontend never sees access/refresh tokens - they are encrypted at rest.
 * When edge functions need to make API calls, they use getValidAccessToken()
 * from tokenManager.ts which automatically refreshes expired tokens.
 */